import { hasNonNull } from "@msuite/utils/array";
import { Menubar } from "radix-ui";
import { matchPath, useLocation, useNavigate } from "react-router";
import { Divider } from "../../divider";
import { Icon } from "../../icons";
import { cn } from "../../utils";
import type { NavigationItemType } from "../types";
import { TopbarNavigationSubItem } from "./topbar-navigation-sub-item";

/** Props Interface */
interface TopbarNavigationItemProps {
	navigationItem: NavigationItemType;
}

export function TopbarNavigationItem({ navigationItem }: TopbarNavigationItemProps) {
	/** Hooks */
	const navigate = useNavigate();
	const { pathname } = useLocation();

	/** Computed */
	const hasItems = hasNonNull(navigationItem.items);

	function isRouteActive(to: string) {
		return !!matchPath({ path: to, end: false }, pathname);
	}

	const isActive = navigationItem.to
		? isRouteActive(navigationItem.to)
		: (navigationItem.items?.some(
				(item) => typeof item !== "string" && !!item.to && isRouteActive(item.to),
			) ?? false);

	/** Functions */
	function handleOnNavigate() {
		if (!navigationItem.to) return;
		navigate(navigationItem.to);
	}

	/** Render */
	return (
		<Menubar.Menu>
			<Menubar.Trigger
				className={cn(
					"text-sm",
					"h-stack select-none pr-2 pl-2.5 py-1 outline-none",
					"rounded-base",
					"cursor-pointer",
					isActive && "bg-white dark:bg-neutral-900 shadow-xs border-[0.5px] border-color",
				)}
				onClick={handleOnNavigate}
				data-cy={`navigation-${navigationItem.label}`.toLowerCase()}
			>
				{navigationItem.label} {hasNonNull(navigationItem.items) && <Icon name="ChevronDown" />}
			</Menubar.Trigger>
			<Menubar.Portal>
				<Menubar.Content
					asChild
					className={cn(hasItems ? "sm:w-auto relative pt-2" : "p-4 bg-white w-full")}
				>
					{hasItems && (
						<div>
							<div className="card inset-x-0 top-2 bottom-0 absolute z-1000" />
							<ul className="v-stack gap-0 list-none p-1 sm:w-max min-w-[200px] relative z-1001">
								{navigationItem.items?.map((item) =>
									typeof item === "string" ? (
										item === "divider" ? (
											<Divider key={Math.random()} />
										) : (
											<div
												className="px-2 my-1"
												key={item}
												tabIndex={-1}
											>
												<p className="label font-normal opacity-50">{item}</p>
											</div>
										)
									) : (
										<TopbarNavigationSubItem
											Icon={item.icon}
											href={item.to!}
											title={item.label}
											key={item.to ?? item.label}
										/>
									),
								)}
							</ul>
						</div>
					)}
				</Menubar.Content>
			</Menubar.Portal>
		</Menubar.Menu>
	);
}
